

import { useEffect, useState } from 'react';
import ReactFlow, {
  Controls,
  MiniMap,
  Background,
  Panel,
} from 'reactflow';
import { Button, Group, Stack, Text, ActionIcon, Tooltip, Modal, Box, Menu, TextInput, Textarea, Tabs } from '@mantine/core';
import { IconDownload, IconDeviceFloppy, IconPlayerPlay, IconMaximize, IconChevronRight, IconChevronDown, IconGitBranch, IconArrowBackUp, IconArrowForwardUp, IconShare, IconVariable, IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { FlowDSL } from '@holloway/dsl';
import { variablesApi } from '@/services/variables.api';
import NodePalette from './NodePalette';
import PropertyPanel from './PropertyPanel';
import SimulatorPanel from './SimulatorPanel';
import ShareLinksModal from './ShareLinksModal';
import VariablesPanel from './VariablesPanel';
import { nodeTypes } from './nodes';

// Custom hooks
import { useFlowEditorState } from './FlowEditor/useFlowEditorState';
import { useFlowOperations } from './FlowEditor/useFlowOperations';
import { useNodeDragDrop } from './FlowEditor/useNodeDragDrop';
import { useFlowPersistence } from './FlowEditor/useFlowPersistence';
import { useFlowHistory } from './FlowEditor/useFlowHistory';

/**
 * Padding applied by fitView — used for both the initial fit on load and the
 * Controls "fit view" button.  Keeping one constant ensures they behave the
 * same way: 0.2 gives a 20 % margin on every side so the top-most node never
 * lands under the Save/toolbar panel at the top of the canvas.
 */
const FIT_VIEW_OPTIONS = { padding: 0.2 } as const;

interface Props {
  flowId: string;
  flowName?: string;
  initialFlow?: FlowDSL;
  currentVersion?: any;
  versions?: any[];
  publishedVersionId?: string;
  /** Templates are never shared, so the editor offers no share links for them. */
  isTemplate?: boolean;
}

export default function FlowEditor({ flowId, flowName, initialFlow, currentVersion, versions = [], publishedVersionId, isTemplate = false }: Props) {
  // Modal states
  const [newVersionModalOpened, setNewVersionModalOpened] = useState(false);
  const [shareLinksModalOpened, setShareLinksModalOpened] = useState(false);
  const [versionComment, setVersionComment] = useState('');
  const [saveAfterStateCommit, setSaveAfterStateCommit] = useState(false);

  // State management
  const {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    selectedNode,
    setSelectedNode,
    simulatorOpen,
    setSimulatorOpen,
    simulatorFullscreen,
    setSimulatorFullscreen,
    hasUnsavedChanges,
    resetUnsavedChanges,
  } = useFlowEditorState({ initialFlow });

  // Node and edge operations
  const {
    onConnect,
    addNode,
    deleteNode,
    updateNodeData,
    handleNodeClick,
  } = useFlowOperations({
    nodes,
    setNodes,
    setEdges,
    setSelectedNode,
  });

  // Drag and drop
  const { handleNodeDragStop } = useNodeDragDrop({
    nodes,
    updateNodeData,
  });

  // Flow variables (GUID-keyed; source of truth for the embedded DSL defs)
  const { data: variables = [] } = useQuery({
    queryKey: ['variables', 'flow', flowId],
    queryFn: () => variablesApi.getFlowVariables(flowId),
  });

  // Persistence (save, export, etc.)
  const { saveFlow, createNewVersion, exportJSON, getCurrentFlowDSL, saveSize } = useFlowPersistence({
    flowId,
    currentVersionId: currentVersion?.id,
    nodes,
    edges,
    variables,
    resetUnsavedChanges,
  });

  useEffect(() => {
    if (!saveAfterStateCommit) return;

    setSaveAfterStateCommit(false);
    void saveFlow();
  }, [saveAfterStateCommit, saveFlow]);

  // Undo/Redo history
  const { undo, redo, canUndo, canRedo } = useFlowHistory({
    nodes,
    edges,
    setNodes,
    setEdges,
  });

  return (
    <>
      <div style={{ display: 'flex', height: 'calc(100vh - 80px)' }}>
        {/* Left Panel - Nodes & Variables */}
        <div style={{ width: 220, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
          <Tabs defaultValue="nodes" styles={{ root: { display: 'flex', flexDirection: 'column', height: '100%' }, panel: { flex: 1, overflow: 'auto', padding: '0.75rem' } }}>
            <Tabs.List grow>
              <Tabs.Tab value="nodes" fz="xs">Nodes</Tabs.Tab>
              <Tabs.Tab value="variables" fz="xs" leftSection={<IconVariable size={14} />}>Variables</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="nodes">
              <NodePalette onAdd={addNode} />
            </Tabs.Panel>
            <Tabs.Panel value="variables">
              <VariablesPanel flowId={flowId} nodes={nodes} />
            </Tabs.Panel>
          </Tabs>
        </div>

        {/* Center Canvas */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={handleNodeClick}
            onNodeDragStop={handleNodeDragStop}
            nodeTypes={nodeTypes}
            fitViewOptions={FIT_VIEW_OPTIONS}
            onInit={(instance) => {
              requestAnimationFrame(() => instance.fitView(FIT_VIEW_OPTIONS));
            }}
            deleteKeyCode={['Backspace', 'Delete']}
          >
            <Controls />
            <MiniMap />
            <Background />
            <Panel position="top-right">
              <Group>
                {/* Undo/Redo buttons */}
                <Button.Group>
                  <Tooltip label="Undo (Ctrl+Z)" withArrow>
                    <ActionIcon
                      variant="light"
                      size="lg"
                      onClick={undo}
                      disabled={!canUndo}
                    >
                      <IconArrowBackUp size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Redo (Ctrl+Shift+Z)" withArrow>
                    <ActionIcon
                      variant="light"
                      size="lg"
                      onClick={redo}
                      disabled={!canRedo}
                    >
                      <IconArrowForwardUp size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Button.Group>

                {/* Only appears past half the server's size limit, so an author
                    learns the flow is getting large while there is still room
                    to act rather than at the save that fails. */}
                {saveSize && (
                  <Tooltip
                    label={`This flow is ${saveSize.percentOfLimit}% of the maximum size a flow can be saved at. Shorten the text on its longest pages, or split it into separate flows.`}
                    multiline
                    w={260}
                  >
                    <Text
                      size="xs"
                      c={saveSize.critical ? 'red' : 'dimmed'}
                      fw={saveSize.critical ? 600 : 400}
                      style={{ whiteSpace: 'nowrap', cursor: 'default' }}
                    >
                      {saveSize.megabytes} MB
                    </Text>
                  </Tooltip>
                )}

                {/* Split save button with dropdown */}
                <Button.Group>
                  <Button
                    size="xs"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={saveFlow}
                    color={hasUnsavedChanges ? 'blue' : 'gray'}
                    variant={hasUnsavedChanges ? 'filled' : 'light'}
                  >
                    {hasUnsavedChanges ? 'Save*' : 'Saved'}
                  </Button>
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <Button
                        size="xs"
                        px={8}
                        color={hasUnsavedChanges ? 'blue' : 'gray'}
                        variant={hasUnsavedChanges ? 'filled' : 'light'}
                      >
                        <IconChevronDown size={14} />
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconGitBranch size={14} />}
                        onClick={() => setNewVersionModalOpened(true)}
                      >
                        Save New Version
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Button.Group>

                <Tooltip label="Export JSON">
                  <ActionIcon onClick={exportJSON} variant="light" size="lg">
                    <IconDownload size={16} />
                  </ActionIcon>
                </Tooltip>
                {!isTemplate && (
                  <Tooltip label="Share Flow">
                    <ActionIcon onClick={() => setShareLinksModalOpened(true)} variant="light" size="lg" color="blue" aria-label="Share Flow">
                      <IconShare size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                <Tooltip label="Test Flow">
                  <ActionIcon onClick={() => setSimulatorOpen(true)} variant="light" size="lg" color="green" aria-label="Test Flow">
                    <IconPlayerPlay size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Panel>
            <Panel position="bottom-right">
              <Tooltip label="Add Page" withArrow position="left">
                <ActionIcon
                  aria-label="Add Page"
                  size={52}
                  radius="xl"
                  variant="filled"
                  color="#415A91"
                  onClick={() => addNode('Page')}
                  style={{
                    boxShadow: '0 12px 28px rgba(65, 90, 145, 0.28)',
                  }}
                >
                  <IconPlus size={26} />
                </ActionIcon>
              </Tooltip>
            </Panel>
          </ReactFlow>
        </div>

        {/* Right Property Panel */}
        {selectedNode && !simulatorOpen && (
          <div style={{ width: 400, borderLeft: '1px solid #e0e0e0', overflowY: 'auto', padding: '1rem' }}>
            <PropertyPanel
              node={selectedNode}
              nodes={nodes}
              onUpdate={(data) => updateNodeData(selectedNode.id, data)}
              onDelete={deleteNode}
              onAddNode={addNode}
              onClose={() => setSelectedNode(null)}
              onSaveFlow={() => setSaveAfterStateCommit(true)}
              flowId={flowId}
            />
          </div>
        )}
      </div>

      {/* Simulator Modal */}
      <Modal
        opened={simulatorOpen}
        onClose={() => {
          setSimulatorOpen(false);
          setSimulatorFullscreen(false);
        }}
        title={
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text fw={600}>Flow Simulator</Text>
            <Group gap="xs">
              <Tooltip label={simulatorFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                <ActionIcon
                  variant="subtle"
                  onClick={() => setSimulatorFullscreen(!simulatorFullscreen)}
                >
                  {simulatorFullscreen ? <IconChevronRight size={18} /> : <IconMaximize size={18} />}
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        }
        size={simulatorFullscreen ? '100%' : 'xl'}
        fullScreen={simulatorFullscreen}
        styles={{
          body: { height: simulatorFullscreen ? 'calc(100vh - 80px)' : '600px' },
        }}
      >
        <SimulatorPanel flowDSL={getCurrentFlowDSL()} />
      </Modal>

      {/* New Version Modal */}
      <Modal
        opened={newVersionModalOpened}
        onClose={() => {
          setNewVersionModalOpened(false);
          setVersionComment('');
        }}
        title="Create New Version"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Creating a new version will save the current flow state as version {(currentVersion?.version || 0) + 1}.
          </Text>

          <Textarea
            label="Version Comment"
            placeholder="What changed in this version?"
            value={versionComment}
            onChange={(e) => setVersionComment(e.currentTarget.value)}
            minRows={3}
            autoFocus
          />

          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              onClick={() => {
                setNewVersionModalOpened(false);
                setVersionComment('');
              }}
            >
              Cancel
            </Button>
            <Button
              leftSection={<IconGitBranch size={16} />}
              onClick={async () => {
                await createNewVersion(versionComment);
                setNewVersionModalOpened(false);
                setVersionComment('');
              }}
            >
              Create Version
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Share Links Modal */}
      <ShareLinksModal
        opened={shareLinksModalOpened}
        onClose={() => setShareLinksModalOpened(false)}
        flowId={flowId}
        versions={versions}
      />
    </>
  );
}
